/**
 * One generated build at a stable, public URL. While the agent works the page polls; once it
 * is ready it renders the talents, the levelling order, the gear, the extras and the agent's
 * own reasoning. Anyone can read it; only the owner sees Regenerate, and any other logged-in
 * reader can save it into their "Your builds" list on the build tab.
 */
import { useParams, useSearchParams } from 'react-router';
import { BUILD_CONTENT_LABELS, BUILD_SCOPE_LABELS, type BuildSlot, type BuildView, pageTitle } from '@thottbot/shared';
import { useMe } from '../api/client';
import { useBuild, useRegenerateBuild, useSaveBuild, useSetGearSelection } from '../api/builds';
import { ArtifactLink } from '../components/ArtifactLink';
import { Badge } from '../components/Badge';
import { TalentTrees } from '../features/characters/TalentTrees';
import { GearTable } from '../features/build/GearTable';
import { BuildSheet } from '../features/build/BuildSheet';
import { NotFound } from './NotFound';
import { describeBuildError } from './Build';
import { relativeTime } from '../util/time';

const STATUS_TEXT: Record<BuildView['status'], string> = {
  queued: 'Waiting for a free slot',
  generating: 'Generating your build',
  ready: 'Ready',
  failed: 'This build failed',
};

export function BuildPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const build = useBuild(id ?? null);
  const me = useMe();
  const regenerate = useRegenerateBuild();
  const save = useSaveBuild();
  const gearSelection = useSetGearSelection(id ?? '');

  if (build.isPending) return <div className="page muted">Loading...</div>;
  if (build.isError) {
    if (build.error.status === 404) return <NotFound message="No such build." />;
    if (build.error.status === 410) return <NotFound message="This build was removed." />;
    return <div className="page error">Could not load this build.</div>;
  }

  const b = build.data;
  const mine = me.data?.user?.id === b.ownerId;
  const working = b.status === 'queued' || b.status === 'generating';
  const { character } = b;
  const gearError =
    gearSelection.error && gearSelection.variables ? { slot: gearSelection.variables.slot, message: describeBuildError(gearSelection.error) } : null;
  const gearEditing = {
    editable: mine && b.status === 'ready' && !b.removed,
    onSelect: (slot: BuildSlot, artifactId: string | null) => gearSelection.mutate({ slot, artifactId }),
    error: gearError,
  };
  const gearTable = b.result ? <GearTable gear={b.result.gear} {...gearEditing} /> : null;

  return (
    <div className="page" data-testid="build-page" data-status={b.status}>
      <title>{pageTitle(`${character.race} ${character.class} build`)}</title>
      <h1>
        Level {character.level} {character.race} {character.class} &mdash; {character.role}
      </h1>
      {character.spec && (
        <p data-testid="build-spec">
          {character.spec} &middot; {BUILD_CONTENT_LABELS[character.content]}
        </p>
      )}
      <p className="muted">
        {character.characterName && (
          <>
            For {character.characterName}.{' '}
          </>
        )}
        Generated {relativeTime(b.createdAt)}. Gear scope: {BUILD_SCOPE_LABELS[b.scope]}.
      </p>

      {params.get('reused') === '1' && (
        <p className="notice" data-testid="build-reused">
          Served from a build generated {relativeTime(b.createdAt)}.
        </p>
      )}

      <p data-testid="build-status">{STATUS_TEXT[b.status]}</p>
      {working && <p className="muted">This can take a minute or two. The page updates itself.</p>}
      {b.status === 'failed' && b.error && <p className="notice error">{b.error}</p>}

      {mine && !working && (
        <p className="actions">
          <button
            type="button"
            disabled={regenerate.isPending}
            onClick={() =>
              regenerate.mutate(b.id, {
                onSuccess: (res) => {
                  window.location.href = `/build/${res.build.id}`;
                },
              })
            }
          >
            {regenerate.isPending ? 'Starting...' : 'Regenerate'}
          </button>
          {regenerate.error && <span className="error"> {describeBuildError(regenerate.error)}</span>}
        </p>
      )}

      {me.data?.user && !mine && !b.removed && (
        <p className="actions">
          <button
            type="button"
            data-testid="build-save"
            aria-pressed={!!b.saved}
            disabled={save.isPending}
            onClick={() => save.mutate({ id: b.id, saved: !b.saved })}
          >
            {b.saved ? 'Saved' : 'Save build'}
          </button>
          {b.saved && <span className="muted"> Saved builds appear on the build tab.</span>}
          {save.error && <span className="error"> {describeBuildError(save.error)}</span>}
        </p>
      )}

      {b.result && (
        <>
          <section className="section">
            <h2>Stat priority</h2>
            <p>{b.result.statPriority.join(' > ')}</p>
          </section>

          <section className="section">
            <h2>Talents</h2>
            <p>{b.result.talents.summary}</p>
            {b.talentsforeverUrl ? (
              <p className="actions">
                <a className="button" href={b.talentsforeverUrl} target="_blank" rel="noopener" data-testid="build-talentsforever">
                  Open in talentsforever
                </a>
              </p>
            ) : (
              <p className="muted" data-testid="build-talents-legacy">
                This build was planned before the site's talent trees came from talentsforever.com, so it can't be opened there.
                {mine && ' Regenerate it for the current trees.'}
              </p>
            )}
            {/* The character page's in-game window; it carries the talentsforever credit itself. */}
            <TalentTrees talents={{ specialization: null, trees: b.result.talentWindow }} />
          </section>

          <section className="section">
            <h2>Levelling order</h2>
            <div className="table-scroll">
              <table className="grid" data-testid="build-order">
                <thead>
                  <tr>
                    <th>Levels</th>
                    <th>Talent</th>
                    <th>Points</th>
                  </tr>
                </thead>
                <tbody>
                  {b.result.talents.order.map((step, i) => (
                    <tr key={`${step.talent.artifactId}-${i}`}>
                      <td>
                        {step.fromLevel === step.toLevel ? step.fromLevel : `${step.fromLevel} to ${step.toLevel}`}
                      </td>
                      <td>
                        <ArtifactLink type="ability" slug={step.talent.slug} name={step.talent.name} />
                      </td>
                      <td>{step.points}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className="section">
            <h2>Gear</h2>
            {b.result.sheet ? (
              <>
                <BuildSheet character={character} gear={b.result.gear} sheet={b.result.sheet} {...gearEditing} />
                {/* Enchants, sources and alternatives in full, for touch and screen readers. */}
                <details className="build-gear-details">
                  <summary>Gear details</summary>
                  {gearTable}
                </details>
              </>
            ) : (
              gearTable
            )}
          </section>

          <section className="section" data-testid="build-consumables">
            <h2>Consumables and buffs</h2>
            {b.result.consumables.length === 0 && b.result.worldBuffs.length === 0 ? (
              <p className="muted">Nothing worth carrying at this level.</p>
            ) : (
              <ul>
                {b.result.consumables.map((c) => (
                  <li key={c.artifactId}>
                    <ArtifactLink type="item" slug={c.slug} name={c.name} /> <Badge status={c.dataStatus} build={c.sourceBuild} />
                    <div className="muted">{c.when}</div>
                  </li>
                ))}
                {b.result.worldBuffs.map((w) => (
                  <li key={w.spellId}>
                    {w.name}
                    <div className="muted">{w.why}</div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="section" data-testid="build-professions">
            <h2>Professions</h2>
            {b.result.professions.length === 0 ? (
              <p className="muted">No profession is worth the detour for this build.</p>
            ) : (
              b.result.professions.map((p) => (
                <div key={p.name} className="profession">
                  <h3>{p.name}</h3>
                  <p>{p.why}</p>
                  {p.recipes.length > 0 && (
                    <ul>
                      {p.recipes.map((r) => (
                        <li key={r.artifactId}>
                          <ArtifactLink type="recipe" slug={r.slug} name={r.name} />
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))
            )}
          </section>

          <section className="section">
            <h2>Reasoning</h2>
            <p>{b.result.reasoning}</p>
            {b.result.gaps.length > 0 && (
              <>
                <h3>What the database could not decide</h3>
                <ul data-testid="build-gaps">
                  {b.result.gaps.map((g) => (
                    <li key={g}>{g}</li>
                  ))}
                </ul>
              </>
            )}
          </section>
        </>
      )}

      <p className="muted" data-testid="build-footer">
        Built from Forever data {b.dataBuild}. Gear scope: {BUILD_SCOPE_LABELS[b.scope]}.
      </p>
    </div>
  );
}
